const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Dates are ISO "YYYY-MM-DD" strings; parse as local dates to avoid timezone shifts.
export const parseDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const toISO = (date) => {
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
};

export const addDays = (iso, days) => {
  const date = parseDate(iso);
  date.setDate(date.getDate() + days);
  return toISO(date);
};

export const formatDate = (iso) => {
  if (!iso) return "—";
  const d = parseDate(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

export const formatDateShort = (iso) => {
  const d = parseDate(iso);
  return `${DAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

export const formatDateLong = (iso) => {
  const d = parseDate(iso);
  return `${DAYS_LONG[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

export const dayName = (iso) => DAYS[parseDate(iso).getDay()];

export const timeToMinutes = (time) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

export const minutesToTime = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export const formatTime = (time) => {
  const mins = timeToMinutes(time);
  const h24 = Math.floor(mins / 60);
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const m = String(mins % 60).padStart(2, "0");
  return `${h12}:${m} ${h24 < 12 ? "AM" : "PM"}`;
};

export const formatRM = (amount) =>
  `RM ${Number(amount).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const initials = (name) =>
  name
    .replace(/^Dr\.?\s+/i, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

export const ageFrom = (dob, todayIso) => {
  const birth = parseDate(dob);
  const today = parseDate(todayIso);
  let age = today.getFullYear() - birth.getFullYear();
  const beforeBirthday =
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate());
  if (beforeBirthday) age -= 1;
  return age;
};

// MyKad numbers are YYMMDD-PB-####. Show the birth date and place-of-birth code, mask the last 4 digits.
// Anything that isn't a 12-digit IC (e.g. a passport number) keeps only its last 3 characters visible.
export const formatIC = (ic) => {
  if (!ic) return "—";
  const d = ic.replace(/\D/g, "");
  if (d.length === 12) return `${d.slice(0, 6)}-${d.slice(6, 8)}-XXXX`;
  return ic.length > 3 ? `${"•".repeat(ic.length - 3)}${ic.slice(-3)}` : ic;
};

// Formats IC input as it's typed: 900514135218 -> 900514-13-5218. Leaves passport numbers untouched.
export const formatICInput = (value) => {
  if (/[a-z]/i.test(value)) return value.toUpperCase();
  const d = value.replace(/\D/g, "").slice(0, 12);
  if (d.length <= 6) return d;
  if (d.length <= 8) return `${d.slice(0, 6)}-${d.slice(6)}`;
  return `${d.slice(0, 6)}-${d.slice(6, 8)}-${d.slice(8)}`;
};

// Date of birth from a MyKad number (YYMMDD). Two-digit years after the current year are treated as 1900s.
export const dobFromIC = (ic, todayIso) => {
  const d = (ic || "").replace(/\D/g, "");
  if (d.length < 6) return "";
  const yy = Number(d.slice(0, 2));
  const mm = d.slice(2, 4);
  const dd = d.slice(4, 6);
  if (Number(mm) < 1 || Number(mm) > 12 || Number(dd) < 1 || Number(dd) > 31) return "";
  const currentYY = Number(todayIso.slice(2, 4));
  const year = yy > currentYY ? 1900 + yy : 2000 + yy;
  return `${year}-${mm}-${dd}`;
};
